/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Code } from '@carbon/react/icons';
import { TextInput, CodeSnippet, FormLabel } from 'carbon-components-react';
import cx from 'classnames';
import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { TabContent } from '../../common/tabbed-header/TabContent';
import { sharedMessages } from '../../messages';
import { links } from '../links';
import { FIELDS } from '../schema';
import { ClickToChat as messages } from './messages';

export const ClickToChat = ({ readOnly }: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();
  const formMethods = useFormContext();
  const { errorMessageIntl, getFieldValue, registerInputField } =
    useFormHelpers(formMethods);

  const phoneNumberChangeState = useState<boolean>(false);
  const inviteUserChangeState = useState<boolean>(false);
  const [embedLink, setEmbedLink] = useState<string>('');
  const [embedLinkDisabled, setEmbedLinkDisabled] = useState<boolean>(true);

  const phoneNumber = getFieldValue(FIELDS.PHONE_NUMBER);
  const fieldMessage = getFieldValue(FIELDS.INVITE_USER_TO_OPEN_MESSAGE);

  // Update embedLink state
  useEffect(() => {
    // Update if should be disabled or not.
    setEmbedLinkDisabled(
      phoneNumber === '' || errorMessageIntl(FIELDS.PHONE_NUMBER) ? true : false
    );

    const encodedMessage =
      fieldMessage !== ''
        ? encodeURI(fieldMessage)
        : encodeURI(t(messages.defaultChatMessage));
    setEmbedLink(
      `https://api.whatsapp.com/send?phone=` +
        phoneNumber +
        `&text=` +
        encodedMessage
    );
  }, [errorMessageIntl, fieldMessage, t, phoneNumber]);

  return (
    <TabContent
      ImageComponent={() => <Code size={32} />}
      title={<FormattedMessage {...messages.title} />}>
      <div className="TabContent__body-content">
        <div className="TabContent__subtitle TabContent__link-larger-text">
          <FormattedMessageWithLink
            url={
              useIsNewIA()
                ? links.learnMoreWhatsAppClickToChatNewIA
                : links.learnMoreWhatsAppClickToChatLink
            }
            {...messages.subtitle}
          />
        </div>
        <div className="TabContent__field-container">
          <TextInput
            {...registerInputField({
              field: FIELDS.PHONE_NUMBER,
              labelText: messages.phoneNumberLabel,
              isPasswordField: false,
              placeholderText: messages.phoneNumberPlaceholder,
              changeState: phoneNumberChangeState,
              readOnly
            })}
          />
          <div className="TabContent__spacer24" />
          <TextInput
            {...registerInputField({
              field: FIELDS.INVITE_USER_TO_OPEN_MESSAGE,
              labelText: messages.messageLabel,
              placeholderText: messages.messagePlaceholder,
              changeState: inviteUserChangeState,
              readOnly
            })}
          />
        </div>
        <div className="TabContent__spacer24" />
        <div
          className={cx('TabContent__field-container', {
            'TabContent__field--disabled': embedLinkDisabled
          })}>
          <h4>
            <FormattedMessage {...messages.embedLinkTitle} />
          </h4>
          <div className="TabContent__spacer8" />
          <div className="TabContent__subtitle">
            <FormattedMessage {...messages.embedLinkDescription} />
          </div>
          <FormLabel
            className={cx({
              'TabContent__field--disabled': embedLinkDisabled
            })}>
            <FormattedMessage {...messages.embedLinkLabel} />
          </FormLabel>
          <CodeSnippet
            className={cx({
              'TabContent__field--disabled': embedLinkDisabled
            })}
            copyButtonDescription={t(sharedMessages.copyToClipboard)}
            copyLabel={t(sharedMessages.copyToClipboard)}
            feedback={t(sharedMessages.copied)}
            hideCopyButton={embedLinkDisabled || readOnly}
            onClick={() => {
              copyContentToClipboard(embedLink);
              ctaEvents.whatsAppEmbedLinkCopied();
            }}
            type="single">
            {!embedLinkDisabled ? embedLink : ''}
          </CodeSnippet>
        </div>
      </div>
    </TabContent>
  );
};
